import classNames from 'classnames'
import * as EmailValidator from 'email-validator'
import { Link } from 'gatsby'
import React, { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input/input'
import * as Styles from './form.module.scss'
import { PRIVACY_POLICY_URL } from '@/constants/url'
import { VALIDATION_ERROR_MESSAGE } from '@/constants/validation-error-message'
import { pagesPath } from '@/lib/pagesPath'
import { useValidationForm } from '@/lib/use-validation-form'

const defaultFormState = {
  contract: '未契約',
  name: '',
  'name-kana': '',
  organization: '',
  section: '',
  position: '',
  tel: '',
  email: '',
  category: '',
  content: '',
  agree_flag: false,
}

export function ContactForm({
  initFormState,
  submitHandler: _submitHandler,
  readonly,
  referrerUrl,
  ...props
}) {
  const {
    control,
    handleSubmit,
    errors,
    watchFileds,
    setValue,
    submitHandler,
    validTelNumber,
    convertDomesticNumber,
  } = useValidationForm({
    initFormState: initFormState,
    defaultFormState: defaultFormState,
    _submitHandler: _submitHandler,
  })

  /** サービス契約中が選択されている */
  const isSelectedExisting = useMemo(() => {
    return watchFileds.contract === 'サービス契約中'
  }, [watchFileds.contract])
  return (
    <form
      {...props}
      onSubmit={handleSubmit(submitHandler)}
      className={classNames(Styles.form, props.className)}
    >
      <dl>
        <dt data-require>お名前</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.name}</p>
          ) : (
            <>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.name.require,
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="name"
                    inputMode="text"
                    className={classNames(errors.name?.message && Styles.error)}
                    type="text"
                    placeholder="お名前をご入力ください"
                  />
                )}
              />
              <span className={Styles.errorMessage}>
                {errors.name?.message}
              </span>
            </>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>フリガナ</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds['name-kana']}</p>
          ) : (
            <>
              <Controller
                name="name-kana"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.nameKana.require,
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="name-kana"
                    type="text"
                    className={classNames(
                      errors['name-kana']?.message && Styles.error
                    )}
                    placeholder="フリガナをご入力ください"
                  />
                )}
              />
              <span className={Styles.errorMessage}>
                {errors['name-kana']?.message}
              </span>
            </>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>会社名</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.organization}</p>
          ) : (
            <>
              <Controller
                name="organization"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.organization.require,
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="organization"
                    inputMode="text"
                    className={classNames(
                      errors.organization?.message && Styles.error
                    )}
                    type="text"
                    placeholder="会社名をご入力ください"
                  />
                )}
              />
              <span className={Styles.errorMessage}>
                {errors.organization?.message}
              </span>
            </>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>担当部署</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.section}</p>
          ) : (
            <>
              <Controller
                name="section"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.section.require,
                }}
                render={({ field }) => (
                  <>
                    <label className={Styles.selectLabel}>
                      <select
                        {...field}
                        id="section"
                        className={classNames(
                          !watchFileds.section && Styles.nullValue,
                          errors.section?.message && Styles.error
                        )}
                        onChange={(e) => setValue('section', e.target.value)}
                      >
                        <option value="">選択してください</option>
                        <option value="経理">経理</option>
                        <option value="総務">総務</option>
                        <option value="人事">人事</option>
                        <option value="経営企画">経営企画</option>
                        <option value="IR">IR</option>
                        <option value="労務">労務</option>
                        <option value="その他">その他</option>
                      </select>
                    </label>
                    <span className={Styles.errorMessage}>
                      {errors.section?.message}
                    </span>
                  </>
                )}
              />
            </>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>役職</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.position}</p>
          ) : (
            <dd>
              <Controller
                name="position"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.position.require,
                }}
                render={({ field }) => (
                  <>
                    <label className={Styles.selectLabel}>
                      <select
                        {...field}
                        id="position"
                        className={classNames(
                          !watchFileds.position && Styles.nullValue,
                          errors.position?.message && Styles.error
                        )}
                        onChange={(e) => setValue('position', e.target.value)}
                      >
                        <option value="">選択してください</option>
                        <option value="経営・役員">経営・役員</option>
                        <option value="部長・マネージャー">
                          部長・マネージャー
                        </option>
                        <option value="課長・リーダー">課長・リーダー</option>
                        <option value="一般">一般</option>
                      </select>
                    </label>

                    <span className={Styles.errorMessage}>
                      {errors.position?.message}
                    </span>
                  </>
                )}
              />
            </dd>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>電話番号</dt>
        <dd>
          {readonly ? (
            <p>{convertDomesticNumber(watchFileds.tel)}</p>
          ) : (
            <>
              <Controller
                name="tel"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.tel.require,
                  validate: {
                    validTelNumber: (string) =>
                      validTelNumber(string) ||
                      VALIDATION_ERROR_MESSAGE.tel.validate,
                  },
                }}
                render={({ field }) => (
                  <>
                    <PhoneInput
                      {...field}
                      control={control}
                      id="tel"
                      international={false}
                      country="JP"
                      defaultCountry="JP"
                      className={classNames(
                        errors.tel?.message && Styles.error
                      )}
                      placeholder="03-0000-0000"
                    />
                    <span className={Styles.errorMessage}>
                      {errors.tel?.message}
                    </span>
                  </>
                )}
              />
            </>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>メールアドレス</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.email}</p>
          ) : (
            <>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.email.require,
                  validate: {
                    validEmailFormat: (str) =>
                      EmailValidator.validate(str) ||
                      VALIDATION_ERROR_MESSAGE.email.validate,
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="email"
                    inputMode="email"
                    className={classNames(
                      errors.email?.message && Styles.error
                    )}
                    type="email"
                    placeholder="sample@uniforce.co.jp"
                  />
                )}
              />
              <span className={Styles.errorMessage}>
                {errors.email?.message}
              </span>
            </>
          )}
        </dd>
      </dl>

      <dl>
        <dt data-require>問合せ種別</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.category}</p>
          ) : (
            <Controller
              name="category"
              control={control}
              rules={{
                required: VALIDATION_ERROR_MESSAGE.category.require,
              }}
              render={({ field }) => (
                <>
                  <label className={Styles.selectLabel}>
                    <select
                      {...field}
                      id="category"
                      className={classNames(
                        !watchFileds.category && Styles.nullValue,
                        errors.category?.message && Styles.error
                      )}
                      onChange={(e) => setValue('category', e.target.value)}
                    >
                      <option value="">選択してください</option>
                      {isSelectedExisting ? (
                        <>
                          <option value="ご利用中のサービスの機能について">
                            ご利用中のサービスの機能について
                          </option>
                          <option value="契約内容について">
                            契約内容について
                          </option>
                        </>
                      ) : (
                        <>
                          <option value="Uniforceのサービス内容について">
                            Uniforceのサービス内容について
                          </option>
                          <option value="料金について">料金について</option>
                        </>
                      )}
                      <option value="決算説明資料のデザインサービスについて">
                        決算説明資料のデザインサービスについて
                      </option>
                      <option value="公認会計士のコンサルサービスについて">
                        公認会計士のコンサルサービスについて
                      </option>
                      <option value="その他">その他</option>
                    </select>
                  </label>

                  <span className={Styles.errorMessage}>
                    {errors.category?.message}
                  </span>
                </>
              )}
            />
          )}
        </dd>
      </dl>

      <dl className={Styles.textArea}>
        <dt>その他</dt>
        <dd>
          {readonly ? (
            <p>{watchFileds.content || '記入されていません'}</p>
          ) : (
            <>
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    id="content"
                    placeholder="質問事項などあれば記入ください。"
                  ></textarea>
                )}
              />
            </>
          )}
        </dd>
      </dl>

      <dl className={Styles.consentArea}>
        <dt data-require>個人情報取り扱い</dt>
        <dd>
          {readonly ? (
            <p>プライバシーポリシー・利用規約に同意する。</p>
          ) : (
            <>
              <Controller
                name="agree_flag"
                control={control}
                rules={{
                  required: VALIDATION_ERROR_MESSAGE.agree_flag.require,
                }}
                render={({ field }) => (
                  <label>
                    <input
                      {...field}
                      type="checkbox"
                      name="agree_flag"
                      onChange={(e) => setValue('agree_flag', e.target.checked)}
                    />
                    プライバシーポリシー・利用規約に同意する。
                  </label>
                )}
              />

              <div className={Styles.consentStatement}>
                Uniforceの
                <Link
                  to={pagesPath.rule.$url()}
                >
                  利用規約
                </Link>
                、
                <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                  プライバシーポリシー
                </a>
                をご確認の上、同意をお願いします。
              </div>

              <span className={Styles.errorMessage}>
                {errors.agree_flag?.message}
              </span>
            </>
          )}
        </dd>
      </dl>

      <div className={Styles.buttonArea}>
        {referrerUrl ? <Link to={referrerUrl}>内容を編集する</Link> : null}
        <button type="submit">送信する</button>
      </div>
    </form>
  )
}
